import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { Formik, Field, Form } from "formik";
import { Row, Col, FormGroup, Label, Button } from "reactstrap";
import SimpleReactValidator from "simple-react-validator";
import logoLight from "../../../assets/images/logo-light.webp";
import { __t } from "../../../i18n/translator";
import VendorAuthChips from "./VendorAuthChips";
import {
    clearVendorForgotResponse,
    vendorForgotPasswordRequest,
} from "../../../redux/vendor/auth/forgot/action";
import {
    UserOutlineSvg,
    VendorWaveTopSvg,
    VendorWaveBottomSvg,
    VendorDonutSvg,
} from "../../../components/CommonSvg";

const VendorForgotPassword = (props) => {
    const { forgotVendorPassword, clearForgotResponse, forgotPassApiResponse } = props;
    const validator = useRef(new SimpleReactValidator());
    const [sentToEmail, setSentToEmail] = useState("");

    useEffect(() => {
        clearForgotResponse();
        return () => {
            clearForgotResponse();
        };
    }, []);

    const handleSubmit = (values) => {
        if (validator.current.allValid()) {
            const email = values.email.trim();
            setSentToEmail(email);
            forgotVendorPassword({ email: email });
        } else {
            validator.current.showMessages();
        }
    };

    const isSent = forgotPassApiResponse?.status === true;

    return (
        <div className="vendor-auth">
            <div className="va-wrap">
                <Row className="va-card g-0 mx-0">
                    <Col lg="6" className="va-brand">
                        <VendorWaveTopSvg />
                        <div className="va-glow" />

                        <img className="va-logo" src={logoLight} alt="HRA" />

                        <div className="va-laptop">
                            <div className="va-screen">
                                <div className="va-dash">
                                    <div className="va-side">
                                        <i /><i /><i /><i /><i />
                                    </div>
                                    <div className="va-panel">
                                        <div className="va-t" />
                                        <div className="va-bars">
                                            <b /><b /><b /><b /><b />
                                        </div>
                                    </div>
                                    <div className="va-panel">
                                        <div className="va-t" />
                                        <VendorDonutSvg />
                                        <div className="va-rows">
                                            <i /><i /><i />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="va-base" />
                        </div>

                        <VendorAuthChips />

                        <VendorWaveBottomSvg />
                    </Col>

                    <Col lg="6" className="va-form-side">
                        <h1 className="va-title">Forgot Password</h1>
                        <p className="va-sub">
                            Enter the email address you use for the vendor portal and we will
                            send you a link to reset your password.
                        </p>

                        {isSent ? (
                            <div className="va-sent">
                                <p className="va-sent-text">
                                    A password reset link has been sent to{" "}
                                    <span className="va-email">{sentToEmail}</span>. The link
                                    expires in an hour.
                                </p>
                                <p className="va-sent-note">
                                    Nothing in your inbox? Check the spam folder, or try again
                                    with a different address.
                                </p>

                                <div className="va-row va-row-end">
                                    <Link className="va-forgot" to="/vendor/login">
                                        Back to login
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <Formik initialValues={{ email: "" }} onSubmit={handleSubmit}>
                                {({ values }) => {
                                    return (
                                        <Form role={"form"} className="form-login">
                                            <FormGroup className="va-field">
                                                <Label className="va-label" htmlFor="email">
                                                    Email Address <span className="va-required">*</span>
                                                </Label>
                                                <div className="va-control">
                                                    <span className="va-ic">
                                                        <UserOutlineSvg />
                                                    </span>
                                                    <Field
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        className="va-input"
                                                        placeholder="Enter your email address"
                                                        autoComplete="username"
                                                        maxLength={99}
                                                    />
                                                </div>
                                                {validator.current.message(
                                                    __t("validations.auth.email"),
                                                    values.email,
                                                    "required|email",
                                                    {
                                                        messages: {
                                                            required: __t("validations.auth.email"),
                                                            email: __t("validations.auth.email_valid"),
                                                        },
                                                    }
                                                )}
                                            </FormGroup>

                                            <div className="va-row va-row-end">
                                                <Link className="va-forgot" to="/vendor/login">
                                                    Back to login
                                                </Link>
                                            </div>

                                            <Button type="submit" className="va-btn">
                                                Send Reset Link
                                            </Button>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        )}
                    </Col>
                </Row>
            </div>

            <div className="va-footer">
                <a href="https://www.hraga.com/terms-of-service" target="_blank" rel="noreferrer" title="Terms of Service">
                    Terms of Service
                </a>
                •
                <a href="https://www.hraga.com/mbr-app-privacy-policy" target="_blank" rel="noreferrer" title="Privacy Policy">
                    Privacy Policy
                </a>
                •
                <span className="va-copy">© {new Date().getFullYear()} HRA. All rights reserved.</span>
            </div>
        </div>
    );
};

const mapStateToProps = ({ VendorForgot }) => {
    return {
        forgotPassApiResponse: VendorForgot?.forgotPassApiResponse || null,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        forgotVendorPassword: (data) => dispatch(vendorForgotPasswordRequest(data)),
        clearForgotResponse: () => dispatch(clearVendorForgotResponse()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorForgotPassword);
